import { LoginButton } from '@appcharge/shared-ui';
import './styles.scss';
import { Box } from '@mui/system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isAndroid } from 'react-device-detect';
import IosShareIcon from '@mui/icons-material/IosShare';
import ExploreIcon from '@mui/icons-material/Explore';
import { useTranslation } from 'react-i18next';
import { webStorageUtil } from 'state/webStorage.state.service';
import { useLoginPage } from '../useLoginPage.hook';

export const WebviewEscapeScreen: React.FC = () => {
  const { isProductMadness, publisherMetaData } = useLoginPage();
  const { t } = useTranslation();

  const [showInstructions, setShowInstructions] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [androidLink, setAndroidLink] = useState<string>('');

  const textColor = useMemo(
    () => publisherMetaData.storeTheme.login.textColor || (isProductMadness ? 'black' : 'blue'),
    [publisherMetaData, isProductMadness]
  );

  const textStyle = {
    color: textColor,
  };

  const getRedirectUrl = useCallback(() => {
    const baseAddress = window.location.hostname || window.location.host;
    const sessionData = webStorageUtil.getStorageWithFallback();

    const sessionId = sessionData?.id || '';

    const params = new URLSearchParams(window.location.search);
    params.append('source', 'webview');
    if (sessionId) {
      params.append('sessionId', sessionId);
    }
    const newUrl = `${baseAddress}?${params.toString()}`;

    return newUrl;
  }, [window.location]);

  useEffect(() => {
    const redirectUrl = getRedirectUrl();
    setAndroidLink(`intent://${redirectUrl}#Intent;scheme=https;end`);
  }, [window.location]);

  useEffect(() => {
    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('source', 'webview');

      // Replace the current entry in the browser's history stack
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}?${currentUrl.searchParams.toString()}`
      );
    } catch (err: any) {
      console.error('Error updating URL:', err);
    }
  }, []);

  const openInExternalBrowser = () => {
    const redirectUrl = getRedirectUrl();
    const urlArr = [`googlechrome://${redirectUrl}`];
    urlArr.forEach((url) => {
      try {
        window.open(url);
      } catch (error) {
        console.error(`Failed to open URL in external browser`, error);
      }
    });
    setShowInstructions(true);
    setButtonDisabled(true);
  };

  return (
    <div className={'login__webview-escape-screen'}>
      {showInstructions && (
        <ul className={'login__webview-escape-screen__instructions'}>
          <li className={'login__webview-escape-screen__instructions__item'}>
            <IosShareIcon className={'login__webview-escape-screen__instructions__item__icon'} />
            {t('login.escapeInstructions.stepOne')}
          </li>
          <li className={'login__webview-escape-screen__instructions__item'}>
            <ExploreIcon className={'login__webview-escape-screen__instructions__item__icon'} />
            {t('login.escapeInstructions.stepTwo')}
          </li>
        </ul>
      )}
      <span className={'login__webview-escape-screen__title'} style={textStyle}>
        {t('login.leavingWebviewTitle')}
      </span>
      <Box maxWidth={'400px'} width={'100%'} marginTop={2} marginBottom={0.5}>
        {isAndroid ? (
          <a href={androidLink} style={{ textDecoration: 'none' }}>
            <LoginButton variant={'webview-button'} text={t('login.leavingWebviewButton')} />
          </a>
        ) : (
          <>
            <LoginButton
              variant={'webview-button'}
              text={t('login.leavingWebviewButton')}
              callback={openInExternalBrowser}
              isDisabled={buttonDisabled}
            />
          </>
        )}
      </Box>
      <span className={'login__webview-escape-screen__text'} style={textStyle}>
        {t('login.leavingWebviewDescription')}
      </span>
    </div>
  );
};
